<template>
    <v-container fluid>
      <v-card elevation="2">
        <!-- ——— header ——— -->
        <v-card-title class="d-flex align-center tableheader white--text">
          <v-icon left color="white">mdi-cash-multiple</v-icon>
          Expenses
          <v-spacer/>
          <v-text-field
            v-model="search"
            label="Search (category, unit)"
            prepend-inner-icon="mdi-magnify"
            density="compact"
            variant="solo-filled"
            flat hide-details single-line
            class="me-4"
          />
          <v-btn color="primary" @click="openDialog(null)" v-if="authStore.canEdit">
            <v-icon left>mdi-plus</v-icon> Add Expense
          </v-btn>

          <!-- <ExportButtons entity="expenses" filename="Expenses" :exclude="['id','created_at','updated_at']"></ExportButtons> -->
          <ExportButtons entity="expenses" filename="Expenses" :headers="headers"></ExportButtons>
        </v-card-title>
  
        <!-- ——— table ——— -->
        <v-data-table :items="filtered" :headers="headers" class="elevation-1">
          <template v-slot:[`item.unit_id`]="{ value }">
            {{ findName(units, value) }}
          </template>
          <template v-slot:[`item.expense_date`]="{ value }">
            {{ formatDateForDisplay(value) }}
          </template>
          <template v-slot:[`item.amount`]="{ value }">
            {{ formatCurrency(value) }}
          </template>
          <template v-slot:[`item.vendor_id`]="{ value }">
            {{ value ? findName(vendors, value) : '—' }}
          </template>
          <template v-slot:[`item.category`]="{ value }">
            {{ findCategoryName(value) }}
          </template>
          <template v-slot:[`item.actions`]="{ item }">
            
            <v-tooltip location="top" text="View Details">
            <template v-slot:activator="{ props }">
              <v-icon v-bind="props" small class="me-1" @click="openViewDialog(item)" color="info">
                mdi-eye-outline
              </v-icon>
            </template>
            </v-tooltip>

            <v-tooltip location="top" text="Edit">
            <template v-slot:activator="{ props }">
              <v-icon v-bind="props" small class="me-1" @click="openDialog(item)" color="secondary" v-if="authStore.isAdmin">
                mdi-pencil
              </v-icon>
            </template>
            </v-tooltip>

            <v-tooltip location="top" text="Delete">
            <template v-slot:activator="{ props }">
              <v-icon v-bind="props" small @click="deleteItemPrompt(item)" color="error" v-if="authStore.isAdmin">
                mdi-delete
              </v-icon>
            </template>
            </v-tooltip>

          </template>
          <template v-slot:[`no-data`]>
            <v-alert :value="true" color="info" icon="mdi-information-variant" class="ma-3">
              {{ tableLoading ? 'Fetching data...' : 'No expenses available or match your search.' }}
            </v-alert>
          </template>
        </v-data-table>
      </v-card>
  
      <!-- ——— dialog ——— -->
      <v-dialog v-model="dialog" max-width="500">
        <v-card>
          <v-card-title>{{ form.id ? 'Edit' : 'Add' }} Expense</v-card-title>
          <v-form ref="expenseForm" v-model="isFormValid" @submit.prevent="save">
          <v-card-text>
            <v-select
              :items="[{name: 'None', id: null}, ...units]"
              item-title="name"
              item-value="id"
              v-model="form.unit_id"
              label="Unit"
              variant="outlined"
              class="mb-2"
            />
  
            <v-text-field
              type="date"
              v-model="form.expense_date"
              label="Date*"
              class="mb-2"
              variant="outlined"
              :rules="[rules.required, rules.validDate]"
              required
            />
  
            <v-text-field
              v-model="form.amount"
              type="number"
              label="Amount*"
              :prefix="getCurrencyPrefix()"
              class="mb-2"
              variant="outlined"
              :rules="[rules.required]"
              required
            />
  
            <v-select
              v-model="form.category"
              :items="ExpenseCategoryEnum"
              item-title="label"
              item-value="value"
              label="Category*"
              class="mb-2"
              variant="outlined"
              :rules="[rules.required]"
              required
            />
  
            <v-text-field
              v-model="form.description"
              label="Description (optional)"
              class="mb-2"
              variant="outlined"
            />

            <v-select
              :items="[{name: 'None', id: null}, ...vendors]"
              item-title="name"
              item-value="id"
              v-model="form.vendor_id"
              label="Vendor (optional)"
              clearable
              variant="outlined"
            />

            <!-- <v-select
              :items="companies"
              item-title="name"
              item-value="id"
              v-model="form.company_id"
              label="Company"
            /> -->
          </v-card-text>
          <v-card-actions>
            <v-spacer/>
            <v-btn text @click="dialog = false">Cancel</v-btn>
            <v-btn color="primary" variant="elevated" type="submit" :loading="dialogLoading" :disabled="!isFormValid">Save</v-btn>
          </v-card-actions>
          </v-form>
        </v-card>
      </v-dialog>

      <v-dialog v-model="viewDialog" max-width="800px" @update:modelValue="val => !val && closeViewDialog()">
      <v-card :loading="viewDialogLoading">
        <v-card-title class="tableheader white--text d-flex align-center">
          <v-icon left color="white" class="mr-2">mdi-cash-multiple</v-icon>
          <span class="text-h5">Expense Details: {{ viewItem?.category }}</span>
          <v-spacer></v-spacer>
          <v-btn icon="mdi-close" variant="text" color="white" @click="closeViewDialog"></v-btn>
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text v-if="viewItem" class="pt-4">
          <v-list lines="two" density="compact">
            <!-- <v-list-item>
              <template v-slot:prepend>
                <div class="font-weight-medium">ID:</div>
              </template>
              <v-list-item-text class="text-right pl-2">{{ viewItem.id }}</v-list-item-text>
            </v-list-item><v-divider inset></v-divider> -->
            
            <v-list-item>
              <template v-slot:prepend>
                <div class="font-weight-medium">Date:</div>
              </template>
              <v-list-item-text class="text-right pl-2">{{ formatDateForDisplay(viewItem.expense_date) }}</v-list-item-text>
            </v-list-item><v-divider inset></v-divider>
            <v-list-item>
              <template v-slot:prepend>
                <div class="font-weight-medium">Amount:</div>
              </template>
              <v-list-item-text class="text-right pl-2">{{ formatCurrency(viewItem.amount) }}</v-list-item-text>
            </v-list-item><v-divider inset></v-divider>
            <v-list-item>
              <template v-slot:prepend>
                <div class="font-weight-medium">Category:</div>
              </template>
              <v-list-item-text class="text-right pl-2">{{ findCategoryName(viewItem.category) }}</v-list-item-text>
            </v-list-item><v-divider inset></v-divider>

            <v-list-item>
              <template v-slot:prepend>
                <div class="font-weight-medium">Description:</div>
              </template>
              <v-list-item-text class="text-right pl-2">{{ viewItem.description || 'N/A' }}</v-list-item-text>
            </v-list-item><v-divider inset></v-divider>
            <v-list-item>
              <template v-slot:prepend>
                <div class="font-weight-medium">Unit:</div>
              </template>
              <v-list-item-text class="text-right pl-2">{{ findName(units, viewItem.unit_id) }}</v-list-item-text>
            </v-list-item><v-divider inset></v-divider>
            <v-list-item>
              <template v-slot:prepend>
                <div class="font-weight-medium">Vendor:</div>
              </template>
              <v-list-item-text class="text-right pl-2">{{ findName(vendors, viewItem.vendor_id) }}</v-list-item-text>
            </v-list-item><v-divider inset></v-divider>
            <!-- <v-list-item>
              <template v-slot:prepend>
                <div class="font-weight-medium">Company:</div>
              </template>
              <v-list-item-text class="text-right pl-2">{{ findName(companies, viewItem.company_id) || 'N/A' }}</v-list-item-text>
            </v-list-item><v-divider inset></v-divider> -->
          </v-list>
        </v-card-text>
        <v-card-text v-else class="text-center py-5">
            <v-progress-circular indeterminate color="primary" class="mb-3"></v-progress-circular>
            <p>Loading expense details...</p>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions class="bg-grey-lighten-4 pa-3">
          <v-spacer></v-spacer>
          <v-btn color="primary" variant="tonal" @click="closeViewDialog">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="deleteDialog" max-width="500px">
        <v-card :loading="dialogLoading">
        <v-card-title class="text-h5 error--text">Confirm Deletion</v-card-title>
        <v-card-text>Are you sure you want to delete expense: <strong>{{ itemToDelete?.category }}</strong>?</v-card-text>
        <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="blue-darken-1" text @click="closeDeleteDialog" :disabled="dialogLoading">Cancel</v-btn>
            <v-btn color="error" @click="confirmDelete" :loading="dialogLoading">Delete</v-btn>
            <v-spacer></v-spacer>
        </v-card-actions>
        </v-card>
    </v-dialog>

    <v-snackbar v-model="snackbar.show" :color="snackbar.color" :timeout="snackbar.timeout" bottom right>
      {{ snackbar.message }}
      <template v-slot:actions>
        <v-btn color="white" variant="text" @click="snackbar.show = false">Close</v-btn>
      </template>
    </v-snackbar>

    </v-container>
  </template>
  
  <script setup>
  import { ref, computed, onMounted, nextTick } from 'vue';
  import ExpenseService  from '@/services/ExpenseService';
  import UnitService     from '@/services/UnitService';
  import VendorService   from '@/services/VendorService';
  import { useAuthStore } from '@/stores/auth';
  import { formatDateForDisplay, formatCurrency, getCurrencyPrefix, useSnackbar } from '@/services/genFunctions';
  import ExportButtons from '@/components/ExportButtons.vue';
  // import CompanyService from '@/services/CompaniesService';
  
  // const companies = ref([]);
  const authStore = useAuthStore();
  /* ——— state ——— */
  const expenses = ref([]);
  const units    = ref([]);
  const vendors  = ref([]);

  const viewDialogLoading = ref(false);
  const viewDialog = ref(false);
  const viewItem = ref(null);
  const itemToDelete = ref(null);
  const deleteDialog = ref(false);
  const dialogLoading = ref(false);
  const tableLoading = ref(true);
  const expenseForm = ref(null);
  const isFormValid = ref(false);

  const ExpenseCategoryEnum = [
    { label: 'Plumbing',   value: 'plumbing' },
    { label: 'Fixes',      value: 'fixes' },
    { label: 'Fittings',   value: 'fittings' },
    { label: 'Electrical', value: 'electrical' },
    { label: 'Cleaning',   value: 'cleaning' },
    { label: 'Repairs',    value: 'repairs' },
    { label: 'Condominium fees', value: 'condo_fees' },
    { label: 'Other',      value: 'other' }
  ];

  const rules = {
    required: value => !!value || 'This field is required.',
    positiveNumber: value => (typeof value === 'number' && value > 0) || 'Must be a positive number > 0.',
    validDate: value => /^\d{4}-\d{2}-\d{2}$/.test(value || '') || 'Invalid date format (YYYY-MM-DD).',
    validDateOptional: value => (!value || /^\d{4}-\d{2}-\d{2}$/.test(value)) || 'Invalid date format (YYYY-MM-DD).',
  };

  const { snackbar, showSnackbar } = useSnackbar();

  // const snackbar = ref({ show: false, message: '', color: 'success', timeout: 4000 });
  // const showSnackbar = (message, color = 'success', timeout = 4000) => {
  //   snackbar.value = { message, color, timeout, show: true };
  // };

  const search = ref('');
  const dialog = ref(false);
  const form   = ref(resetForm());
  
  const headers = [
    { title: 'Date',    key: 'expense_date', width:'150' },
    { title: 'Amount',  key: 'amount',   width:'130' },
    { title: 'Category',key: 'category'  },
    { title: 'Description', key: 'description', width:'200' },
    { title: 'Unit',    key: 'unit_id'   },
    { title: 'Vendor',  key: 'vendor_id' },
    { title: 'Actions', key: 'actions',  sortable:false, width:'120' }
  ];
  
  /* ——— lifecycle ——— */

  onMounted(async () => {
    tableLoading.value = true;
    try {
      await loadAll();
    } catch (error) {
      if (error.response && (error.response.status === 401 || error.response.status === 403)) {
          showSnackbar(error.response.data.error || 'You are not authorized to view expenses.', 'error');
      } else {
          showSnackbar('Failed to load expenses.', 'error');
      }
      console.error("Error fetching expenses:", error);
      expenses.value = [];
    } finally {
      tableLoading.value = false;
    }
  });
  
  async function loadAll () {
    [expenses.value, units.value, vendors.value] = await Promise.all([
      ExpenseService.getAll(),
      UnitService.getUnits(),
      VendorService.getVendors()
    ]);
  }
  
  /* ——— helpers ——— */
  function resetForm () {
    return {
      id:null, unit_id:null, expense_date:new Date().toISOString().substr(0,10),
      amount:'', category:'', vendor_id:null
    };
  }
  
  function findName(list, id) {
    const obj = list.find(i => i.id === id);
    return obj ? obj.name : id;
  }
  
  const filtered = computed(() => {
    const q = search.value.toLowerCase();
    return expenses.value.filter(e =>
      e.category.toLowerCase().includes(q) ||
      String(e.unit_id).includes(q)
    );
  });
  
  /* ——— crud ——— */
  function openDialog(item) {
    form.value = item ? { ...item } : resetForm();
    dialog.value = true;
  }

  async function save () {
    if (!expenseForm.value) return;
    const { valid } = await expenseForm.value.validate();
    if (!valid) {
      showSnackbar('Please correct the form errors.', 'warning');
      return;
    }
    const { id, ...data } = form.value;
    id ? await ExpenseService.update(id, data)
       : await ExpenseService.create(data);
    dialog.value = false;
    await loadAll();
    showSnackbar('Expense saved successfully!', 'success');
  }

  const deleteItemPrompt = (item) => {
    itemToDelete.value = { ...item };
    deleteDialog.value = true;
  };

  const closeDeleteDialog = () => {
    deleteDialog.value = false;
    nextTick(() => itemToDelete.value = null);
    dialogLoading.value = false;
  };

  const confirmDelete = async () => {
    if (!itemToDelete.value?.id) return;
    dialogLoading.value = true;
    try {
        await ExpenseService.delete(itemToDelete.value.id);
        expenses.value = expenses.value.filter(e => e.id !== itemToDelete.value.id);
        showSnackbar('Expense deleted successfully!', 'success');
        closeDeleteDialog();
    } catch (error) {
        const errorMsg = error.response?.data?.error || 'Could not delete expense.';
        showSnackbar(errorMsg, 'error', 6000);
        console.error("Error deleting expense:", error.response || error);
    } finally {
        dialogLoading.value = false;
    }
  };

  const openViewDialog = async (item) => {
    viewDialogLoading.value = true;
    viewDialog.value = true;
    viewItem.value = null; 
    try {
      const fullExpenseData = await ExpenseService.get(item.id);
      viewItem.value = fullExpenseData;
    } catch (error) {
      const errorMsg = error.response?.data?.error || 'Failed to load expense details.';
      showSnackbar(errorMsg, 'error');
      console.error("Error fetching expense details for view:", error);
      viewDialog.value = false; 
    } finally {
      viewDialogLoading.value = false;
    }
  };

  const closeViewDialog = () => {
    viewDialog.value = false;
  };

  const findCategoryName = (value) => {
    const category = ExpenseCategoryEnum.find(c => c.value === value);
    return category ? category.label : value;
  };
  </script>
  